import {AnimationFilter} from "@/poster-gallery/classes/advanced-filters/animation-filter";
import {ColorFilter} from "@/poster-gallery/classes/advanced-filters/color-filter";
import {OrientationFilter} from "@/poster-gallery/classes/advanced-filters/orientation-filter";
import {ContentTypeFilter} from "@/poster-gallery/classes/content-type-filter";
import {DisplaySizeFilter} from "@/poster-gallery/classes/display-size-filter";
import {SearchTerm} from "@/poster-gallery/classes/search-term";
import {AdvancedFilterFactory} from "@/poster-gallery/classes/advanced-filters/advanced-filter-factory";
import {AdvancedFilter} from "@/poster-gallery/classes/advanced-filters/advanced-filter";
import {SafeSearchFilter} from "@/poster-gallery/classes/safe-search-filter";
import {DesignStyleFilter} from "@/poster-gallery/classes/advanced-filters/design-style-filter.js";
import {serializeUrl} from "@Utils/url.util";

/**
 * Contains all the filters currently appled to the poster gallery
 */
export class PosterGalleryFilters {
    /**
     * @param {Map<string, AdvancedFilter>} advancedFilters
     * @param {DisplaySizeFilter} displaySizeFilter
     * @param {SearchTerm} searchTermFilter
     * @param {SafeSearchFilter} safeSearchFilter
     */
    constructor(advancedFilters, displaySizeFilter, searchTermFilter, safeSearchFilter) {
        this.advancedFilters = advancedFilters;
        this.displaySizeFilter = displaySizeFilter;
        this.searchTermFilter = searchTermFilter;
        this.safeSearchFilter = safeSearchFilter;
    }

    /**
     * @returns {PosterGalleryFilters}
     */
    static fromPageHtml() {
        const advancedFilters = this.getAdvancedFiltersFromPageHtml();
        const filters = new this(
            advancedFilters,
            DisplaySizeFilter.fromPageHtml(),
            SearchTerm.fromPageHtml(),
            SafeSearchFilter.fromPageHtml(),
        );
        filters.getContentTypeFilter().handleDefaultContentTypeForDisplaySize();
        return filters;
    }

    /**
     * @returns {Map<string, AdvancedFilter>}
     */
    static getAdvancedFiltersFromPageHtml() {
        const advancedFilters = [
            AnimationFilter.fromPageHtml(),
            OrientationFilter.fromPageHtml(),
            ColorFilter.fromPageHtml(),
            ContentTypeFilter.fromPageHtml(),
            DesignStyleFilter.fromPageHtml(),
        ];
        return new Map(advancedFilters.map(filter => [filter.advancedFiltersArrayKey, filter]));
    }

    updateView() {
        this.displaySizeFilter.updateView();
        this.searchTermFilter.updateView();
        this.safeSearchFilter.updateView();

        for (const [, filter] of this.advancedFilters) {
            filter.updateView();
        }
    }

    /**
     * No need to update the search view, the server sends the correct view the first time
     */
    updateViewOnPageLoad() {
        this.displaySizeFilter.updateView();

        for (const [, filter] of this.advancedFilters) {
            filter.updateView();
        }
    }

    /**
     * @param {URL} urlObj
     * @param {boolean} updateContentTypeUrl
     * @return {URL}
     */
    updateUrlForAdvancedFilters(urlObj, updateContentTypeUrl = false) {
        for (const [, filter] of this.advancedFilters) {
            if (!updateContentTypeUrl && filter.advancedFiltersArrayKey === ContentTypeFilter.advancedFiltersArrayKey) {
                continue;
            }
            urlObj = filter.updateAdvancedFilterInUrl(urlObj);
        }
        urlObj = this.safeSearchFilter.updateSafeSearchQueryParam(urlObj);
        return urlObj;
    }

    /**
     * @return {number}
     */
    getCountOfActiveAdvancedFilters() {
        let activeFilters = 0;

        for (const [, filter] of this.advancedFilters) {
            if (filter.isFilterActive()) {
                activeFilters += 1;
            }
        }
        return activeFilters;
    }

    /**
     * @return {number}
     */
    getCountOfActiveAdvancedFiltersWithoutContentType() {
        let activeFilters = 0;

        for (const [, filter] of this.advancedFilters) {
            if (filter.isFilterActive() && filter.advancedFiltersArrayKey !== ContentTypeFilter.advancedFiltersArrayKey) {
                activeFilters += 1;
            }
        }
        return activeFilters;
    }

    /**
     * @return {Object}
     */
    getPopStateObj() {
        const popState = {};

        popState[DisplaySizeFilter.filterType] = this.displaySizeFilter.getPopStateInfo();
        popState[SearchTerm.filterType] = this.searchTermFilter.getPopStateInfo();
        popState[SafeSearchFilter.filterType] = this.safeSearchFilter.getPopStateInfo();

        for (const [, filter] of this.advancedFilters) {
            popState[filter.advancedFiltersArrayKey] = filter.getPopStateInfo();
        }
        return popState;
    }

    /**
     * @param {Object} popStateObj
     */
    updateFiltersFromPopState(popStateObj) {
        this.displaySizeFilter.handleUpdatedFilter(new DisplaySizeFilter(popStateObj[DisplaySizeFilter.filterType]));
        this.searchTermFilter.handleUpdatedFilter(new SearchTerm(popStateObj[SearchTerm.filterType]));
        this.safeSearchFilter.handleUpdatedFilter(new SafeSearchFilter(popStateObj[SafeSearchFilter.filterType]));

        for (const [, filter] of this.advancedFilters) {
            const popStateFilter = AdvancedFilterFactory.fromPopstateObj(filter.advancedFiltersArrayKey, popStateObj[filter.advancedFiltersArrayKey]);
            filter.handleUpdatedFilterFromPopstate(popStateFilter);
        }
    }

    /**
     * @param {PosterGalleryFilters} posterGalleryFilters
     * @return {PosterGalleryFilters}
     */
    static copyGalleryFilters(posterGalleryFilters) {
        const advancedFiltersMap = new Map();

        for (const [key, filter] of posterGalleryFilters.advancedFilters) {
            advancedFiltersMap.set(key, AdvancedFilterFactory.cloneFilter(filter));
        }
        const displaySizeFilter = posterGalleryFilters.displaySizeFilter.cloneFilter(),
            safeSearchFilter = posterGalleryFilters.safeSearchFilter.cloneFilter();
        return new this(
            advancedFiltersMap,
            displaySizeFilter,
            SearchTerm.fromPageHtml(),
            safeSearchFilter
        );
    }

    /**
     * @param {boolean} isLoading
     */
    setLoadingState(isLoading) {
        for (const [, filter] of this.advancedFilters) {
            this.updateFilter(filter, isLoading);
        }
        this.updateFilter(this.displaySizeFilter, isLoading);
        this.updateFilter(this.searchTermFilter, isLoading);
        this.updateFilter(this.safeSearchFilter, isLoading);
    }

    /**
     * @param {AdvancedFilter|DisplaySizeFilter|SafeSearchFilter|SearchTerm} filter
     * @param {boolean} isLoading
     */
    updateFilter(filter, isLoading) {
        if (isLoading) {
            if (filter.wasFilterUpdated()) {
                filter.setLoadingState();
            } else {
                filter.setDisabledState();
            }
        } else {
            filter.setActiveState();
            filter.resetOldFilterState();
        }
        filter.updateView();
    }

    /**
     * @return {ContentTypeFilter}
     */
    getContentTypeFilter() {
        return this.advancedFilters.get(ContentTypeFilter.advancedFiltersArrayKey);
    }

    /**
     * @return {ColorFilter|AdvancedFilter}
     */
    getColorFilter() {
        return this.advancedFilters.get(ColorFilter.advancedFiltersArrayKey);
    }

    /**
     * @return {OrientationFilter|AdvancedFilter}
     */
    getOrientationFilter() {
        return this.advancedFilters.get(OrientationFilter.advancedFiltersArrayKey);
    }

    /**
     * @return {AnimationFilter}
     */
    getMotionFilter() {
        return this.advancedFilters.get(AnimationFilter.advancedFiltersArrayKey);
    }

    /**
     * @return {boolean}
     */
    getCurrentSafeSearchStatus() {
        return this.safeSearchFilter.isFilterActive();
    }

    removeMotionFilter() {
        const motionFilter = this.getMotionFilter(),
            newMotionFilter = new AnimationFilter(AnimationFilter.allFilterOption, motionFilter.advancedFiltersArrayKey);
        newMotionFilter.setWasOptionSelectedByUser(false);
        motionFilter.handleUpdatedFilter(newMotionFilter);
    }

    /**
     * If the user does not have a safe search preference set, we do not set
     * it in the url.
     * This ensures that subsequent ajax refreshes don't enforce
     * a safe search even when the lack of a preference would mean that it should be off.
     *
     * @param {string} url
     * @return {string}
     */
    removeSafeSearchFilterFromUrlIfNeeded(url) {
        if (PMW.posterGallery.isSafeSearchPreferenceSet()) {
            return url;
        }
        return this.safeSearchFilter.removeSafeSearchFilterFromUrl(url);
    }

    /**
     * @param {boolean} removeSizeFilter
     * @return {string}
     */
    removeFilters(removeSizeFilter) {
        let url = window.location.href;

        if (removeSizeFilter) {
            const newSizeFilter = this.displaySizeFilter.cloneFilter();
            newSizeFilter.displaySize = DisplaySizeFilter.allFilterOption;
            this.displaySizeFilter.handleUpdatedFilter(newSizeFilter);
            url = this.displaySizeFilter.getUrlForChosenDisplaySize();
        }
        const urlObj = new URL(url);

        for (const [, filter] of this.advancedFilters) {
            const newFilter = AdvancedFilterFactory.cloneFilter(filter);
            newFilter.setWasOptionSelectedByUser(true);
            newFilter.filterOption = AdvancedFilter.allFilterOption;
            filter.handleUpdatedFilter(newFilter);
        }
        return serializeUrl(this.updateUrlForAdvancedFilters(urlObj, true));
    }
}